<template>
  <div
    :class="['linkooklive', { 'login-user': isLogin, 'page-video-detail': $route.name === 'VideoDetail', 'page-video-list': $route.path === '/video', 'page-commission-list': $route.path === '/commission' }]">
    <linkook-live-header v-if="!hideChrome"></linkook-live-header>
    <router-view class="router-view" />
    <linkook-live-footer v-if="shouldShowFooter && !hideChrome"></linkook-live-footer>

    <!-- 팝업 컴포넌트들 -->
    <login-popup />
    <nickname-popup />
    <terms-notice-popup :is-visible="showTermsNoticePopup" :is-submitting="isSavingTermsNotice"
      @confirm="handleTermsNoticeConfirm" />
    <SimpleModalContainer />
  </div>
</template>
<script>
import LinkookLiveHeader from "@/components/layout/LinkookLiveHeader.vue";
import LinkookLiveFooter from "@/components/layout/LinkookLiveFooter.vue";
import LoginPopup from "@/components/modal/LoginPopup.vue";
import NicknamePopup from "@/components/modal/NicknamePopup.vue";
import TermsNoticePopup from "@/components/modal/TermsNoticePopup.vue";
import SimpleModalContainer from "@/components/modal/SimpleModalContainer.vue";
import scrollManager from "@/utils/scrollManager";
import { ref, runTransaction } from "firebase/database";
import { rtdb } from "/firebaseConfig";
import { useUserStore } from "@/store/user";
import { confirmTermsNotice } from "@/utils/write/user";
import {
  TERMS_NOTICE_VERSION,
  TERMS_NOTICE_TARGET_SIGNUP_BEFORE,
} from "@/utils/termsNotice";
import { initActivityTracker, cleanupActivityTracker, enterPage, ACTIVITY_TYPES } from "@/utils/activityTracker";

export default {
  name: "App",
  components: {
    LinkookLiveHeader,
    LinkookLiveFooter,
    LoginPopup,
    NicknamePopup,
    TermsNoticePopup,
    SimpleModalContainer,
  },
  computed: {
    shouldShowFooter() {
      // 모바일이 아닌 경우 항상 표시
      // if (this.windowWidth > 800) {
      //   return true;
      // }

      // 상세 페이지에서는 footer 숨김
      // const detailPages = ['/live/', '/artist/', '/venue/', '/community/', '/commission/'];
      // const isDetailPage = detailPages.some(page => this.$route.path.startsWith(page));

      // if (isDetailPage) {
      //   return false;
      // }

      // 모바일인 경우 Main Page(/)에서만 표시
      return this.$route.path === "/" || this.$route.path === "/board";
    },
    isAdmin() {
      const userStore = useUserStore();
      return userStore.isLogin && userStore.auth === "admin";
    },
    isLogin() {
      const userStore = useUserStore();
      return userStore.isLogin;
    },
    hideChrome() {
      return !!this.$route.meta?.hideChrome;
    },
  },
  data() {
    return {
      activeConnections: 0,
      onoff: true,
      windowWidth: window.innerWidth,
      versionCheckInterval: null,
      lastBuildHash: null,
      showTermsNoticePopup: false,
      isSavingTermsNotice: false,
    };
  },
  watch: {
    // 화면 크기 변경 감지
    $route(to, from) {
      // 라우트 변경 시 computed 재계산을 위해 강제 업데이트
      this.$forceUpdate();

      // 페이지 체류 시간 측정을 위한 페이지 진입 기록
      this.trackPageChange(to, from);
    },
    async isLogin(newVal) {
      if (newVal) {
        await this.syncTermsNoticeState();
      } else {
        this.showTermsNoticePopup = false;
      }
    },
  },
  methods: {
    parseDateValue(value) {
      if (!value) {
        return null;
      }

      if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
      }

      if (typeof value?.toDate === "function") {
        const parsedDate = value.toDate();
        return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
      }

      if (typeof value?.seconds === "number") {
        const milliseconds =
          value.seconds * 1000 +
          Math.floor((value.nanoseconds || 0) / 1000000);
        const parsedDate = new Date(milliseconds);
        return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
      }

      const parsedDate = new Date(value);
      return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    },
    isTermsNoticeTargetUser() {
      const userStore = useUserStore();
      const joinedAt = this.parseDateValue(
        userStore.joinDate || userStore.createdAt
      );
      const cutoffDate = this.parseDateValue(TERMS_NOTICE_TARGET_SIGNUP_BEFORE);

      if (!cutoffDate || !joinedAt) {
        return true;
      }

      return joinedAt < cutoffDate;
    },
    /** 현재 약관 안내 버전에 대해 서버 기준으로 이미 확인 처리된 상태인지 */
    hasAcknowledgedCurrentTermsNotice() {
      const userStore = useUserStore();
      const confirmedVersion = String(
        userStore.termsNoticeConfirmedVersion || ""
      ).trim();
      if (confirmedVersion !== String(TERMS_NOTICE_VERSION).trim()) {
        return false;
      }
      const confirmedAt = this.parseDateValue(
        userStore.termsNoticeConfirmedAt
      );
      if (!confirmedAt) {
        return false;
      }
      return confirmedAt.getTime() <= Date.now();
    },
    shouldShowTermsNotice() {
      const userStore = useUserStore();
      return (
        userStore.isLogin &&
        this.isTermsNoticeTargetUser() &&
        !this.hasAcknowledgedCurrentTermsNotice()
      );
    },
    async syncTermsNoticeState() {
      const userStore = useUserStore();

      if (!userStore.isLogin || !userStore.email) {
        this.showTermsNoticePopup = false;
        return;
      }

      this.showTermsNoticePopup = false;

      try {
        await userStore.syncUserData({ bypassThrottle: true });
      } catch (error) {
        console.error("이용약관 안내 상태 동기화 실패:", error);
      }

      this.showTermsNoticePopup = this.shouldShowTermsNotice();
    },
    async handleTermsNoticeConfirm() {
      const userStore = useUserStore();

      if (!userStore.email || this.isSavingTermsNotice) {
        return;
      }

      const confirmedAt = new Date().toISOString();
      this.isSavingTermsNotice = true;

      try {
        await confirmTermsNotice(
          userStore.email,
          TERMS_NOTICE_VERSION,
          confirmedAt
        );
        userStore.setTermsNoticeConfirmation(
          TERMS_NOTICE_VERSION,
          confirmedAt
        );
        this.showTermsNoticePopup = false;
      } catch (error) {
        console.error("이용약관 안내 확인 저장 실패:", error);
        this.$alert(
          "이용약관 확인 상태를 저장하지 못했습니다. 잠시 후 다시 시도해주세요."
        );
      } finally {
        this.isSavingTermsNotice = false;
      }
    },
    recordVisitor() {
      // localhost 또는 로컬 개발 환경에서는 방문자 카운팅 제외
      if (window.location.hostname.includes("localhost")) {
        return;
      }

      const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD 형식
      const dailyVisitorRef = ref(rtdb, `visitorCount/${today}`);
      runTransaction(dailyVisitorRef, (currentValue) => {
        return (currentValue || 0) + 1;
      });
      if (this.isAdmin) {
        const dailyAdminRef = ref(rtdb, `adminCount/${today}`);
        runTransaction(dailyAdminRef, (currentValue) => {
          return (currentValue || 0) + 1;
        });
      }
    },
    handleResize() {
      this.windowWidth = window.innerWidth;
    },

    // 새 버전 체크 및 자동 새로고침
    async checkForUpdates() {
      try {
        // 캐시를 우회하여 index.html을 가져옴
        const response = await fetch("/index.html?t=" + Date.now(), {
          cache: "no-cache",
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        });

        const html = await response.text();

        // HTML에서 JS/CSS 파일의 해시값을 추출
        const scriptMatch = html.match(/\/js\/app\.[a-f0-9]+\.js/);
        const currentHash = scriptMatch ? scriptMatch[0] : null;

        if (this.lastBuildHash === null) {
          // 첫 번째 체크: 현재 해시 저장
          this.lastBuildHash = currentHash;
        } else if (currentHash && currentHash !== this.lastBuildHash) {

          // 사용자에게 알림 (선택사항)
          // alert('새로운 버전이 있습니다. 페이지를 새로고침합니다.');

          // 강제 새로고침 (캐시 무시)
          window.location.reload(true);
        }
      } catch (error) {
        console.error("버전 체크 중 오류:", error);
      }
    },

    // 버전 체크 시작
    startVersionCheck() {
      // 개발 환경에서는 실행하지 않음
      if (window.location.hostname.includes("localhost")) {
        return;
      }

      // 초기 체크
      this.checkForUpdates();

      // 30분마다 체크 (30 * 60 * 1000ms = 1800000ms)
      this.versionCheckInterval = setInterval(() => {
        this.checkForUpdates();
      }, 30 * 60 * 1000);
    },

    // 페이지 변경 추적
    trackPageChange(to) {
      // 페이지 이름과 ID 추출
      let pageName = to.name || to.path;
      let pageId = null;
      let activityType = ACTIVITY_TYPES.PAGE_VIEW;

      // 상세 페이지의 경우 ID와 활동 타입 설정
      if (to.name === "LiveDetail" && to.params.slug) {
        pageId = to.params.slug;
        activityType = ACTIVITY_TYPES.LIVE_VIEW;
      } else if (to.name === "ArtistDetail" && to.params.slug) {
        pageId = to.params.slug;
        activityType = ACTIVITY_TYPES.ARTIST_VIEW;
      } else if (to.name === "VenueDetail" && to.params.name) {
        pageId = to.params.name;
        activityType = ACTIVITY_TYPES.VENUE_VIEW;
      } else if (to.name === "BoardDetail" && to.params.boardId) {
        pageId = to.params.boardId;
        activityType = ACTIVITY_TYPES.BOARD_VIEW;
      } else if (to.name === "VideoDetail" && to.params.id) {
        pageId = to.params.id;
        activityType = ACTIVITY_TYPES.VIDEO_VIEW;
      } else if (to.name === "MyProfile") {
        activityType = ACTIVITY_TYPES.PROFILE_VIEW;
      } else if (to.name === "MyCalendar") {
        activityType = ACTIVITY_TYPES.CALENDAR_VIEW;
      } else if (to.name === "MyNotifications") {
        activityType = ACTIVITY_TYPES.NOTIFICATIONS_VIEW;
      } else if (to.params.id) {
        pageId = to.params.id;
      } else if (to.params.name) {
        pageId = to.params.name;
      } else if (to.params.boardId) {
        pageId = to.params.boardId;
      }

      // 페이지 진입 기록 (이전 페이지 체류 시간 자동 계산)
      enterPage(pageName, pageId, activityType);

      // 활동 시간 업데이트 (세션 유지)
      const userStore = useUserStore();
      if (userStore.isLogin) {
        userStore.updateActivity();
      }
    },
  },
  mounted() {
    // 세션 만료 체크 (7일 비활성 시 자동 로그아웃)
    const userStore = useUserStore();
    if (userStore.isLogin && userStore.checkSessionExpiry()) {
      // 세션이 만료된 경우 로그아웃 처리. 인증 필요한 페이지에 있을 때만 홈으로 이동 (직접 URL 입력 시 /artist 등으로 남을 수 있도록)
      if (this.$route.meta.requiresAuth) {
        this.$router.push("/");
      }
    }

    // 컴포넌트가 마운트될 때 방문자 수 증가
    this.recordVisitor();
    // 화면 크기 변경 이벤트 리스너 추가
    window.addEventListener("resize", this.handleResize);
    // 버전 체크 시작
    this.startVersionCheck();
    // 활동 추적 초기화
    initActivityTracker();
    // 초기 페이지 진입 기록
    this.trackPageChange(this.$route, null);
    // 로그인 사용자의 이용약관 안내 노출 여부 동기화
    if (userStore.isLogin) {
      this.syncTermsNoticeState();
    }
  },
  beforeUnmount() {
    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    window.removeEventListener("resize", this.handleResize);
    // 버전 체크 인터벌 정리
    if (this.versionCheckInterval) {
      clearInterval(this.versionCheckInterval);
    }
    // 스크롤 정리 (앱 종료 시 안전장치)
    scrollManager.forceUnlock();
    // 활동 추적 정리
    cleanupActivityTracker();
  },
};
</script>
<style scoped>
@import "./assets/global.css";
</style>
